import type { UnknownAction } from '@reduxjs/toolkit'

import type { WindowDescriptor, WindowSectionPlace } from '@krp/client-runtime'

import { AQSI_KIND } from '../features/connection-form/model/settings'
import { PURPOSE_CAPABILITY, SALES_READ_CAPABILITY } from '../features/source/model/types'
import { REFUNDS_CAPABILITY } from '../features/refunds/model/types'
import { STRUCTURE_CAPABILITY } from '../features/structure/model/types'
import { TRACE_CAPABILITY } from '../features/trace/model/types'
import { catalogIntentOf, catalogPageChanged } from '../features/catalog/model/catalogSlice'
import { PRICES_CAPABILITY } from '../features/prices/model/types'
import { decodeOperationFilter, filterChosen } from '../features/delivery/model/deliverySlice'
import { selectOperationFilter } from '../features/delivery/model/selectors'
import { decodeReceiptsView, viewRestored } from '../features/receipts/model/receiptsSlice'
import { selectReceiptsView } from '../features/receipts/model/selectors'
import { decodeShiftsView, selectShiftsView, shiftsViewRestored } from '../features/shifts/model/shiftsSlice'
import {
  decodeDiscrepancyLimit,
  discrepancyLimitChosen,
  discrepancyLimitOf,
} from '../features/incidents/model/incidentsSlice'
import type { AqsiUiState } from '../state'

/**
 * Окна пакета AQSI (R11). Ресурсные окна — `scope: 'resource'` и `:rid` в
 * каждом адресе: окно AQSI-A и окно AQSI-B — два разных окна, и переключение
 * кабинета не меняет контекст уже открытого окна.
 */
export const AQSI_WINDOWS = {
  catalogSource: 'aqsi.catalog-source',
  rmk: 'aqsi.rmk',
  printSettings: 'aqsi.print-settings',
  catalog: 'aqsi.catalog',
  /** Окно товара источника: карточка кабинета со всеми полями и правка прямо в кабинете. */
  good: 'aqsi.good',
  receipts: 'aqsi.receipts',
  /** «Статистика по продажам» (RP-FB74): сводки поверх тех же общих частей, что и «Чеки». */
  salesStatistics: 'aqsi.sales-statistics',
  /** Раздел «Смены» (решение владельца 26.10.2026): смены кабинета по кассам за период; прежняя вкладка «Мои смены» — его отбор «Только мои». */
  shifts: 'aqsi.shifts',
  receiptAttention: 'aqsi.receipt-attention',
  parkedSales: 'aqsi.parked-sales',
  receipt: 'aqsi.receipt',
  delivery: 'aqsi.delivery',
  /** RP-SYN12: «Создание по умолчанию» — с чем карточка заводится в кабинете при создании из «Синхронизации». */
  createDefaults: 'aqsi.create-defaults',
  // UC1c: сканер и ручная сверка операции. Продажа — этап окна РМК (UI-RMK).
  saleScanner: 'aqsi.sale-scanner',
  saleResolve: 'aqsi.sale-resolve',
  // UC1d: возврат по чеку, журналы инцидентов и расхождений, доказательство восстановления.
  refund: 'aqsi.refund',
  /** Вход в возврат по номеру чека (ввод или скан) - вкладка раздела «Чеки». */
  refundLookup: 'aqsi.refund-lookup',
  /** «Стандартный возврат» из РМК (RP-FB74, этап 2): поиск чека на общих частях «Чеки», дальше окно возврата. */
  refundSearch: 'aqsi.refund-search',
  /** «Возврат по слип-чеку» из РМК (RP-FB74, этап 2): предупреждение и данные слипа. */
  slipRefund: 'aqsi.slip-refund',
  incidents: 'aqsi.incidents',
  recoveryProof: 'aqsi.recovery-proof',
  discrepancies: 'aqsi.discrepancies',
  // П. 9 таблицы замечаний 13.10.2026: магазины кабинета → кассы.
  structure: 'aqsi.structure',
  // П. 6 таблицы замечаний 13.10.2026 (RP-FB9): прайс кабинета во вкладке раздела «Цены».
  prices: 'aqsi.prices',
  // RP-SALE-TRACE: диагностика продаж — где ушло время чека (администратор с правом `sales.trace`).
  trace: 'aqsi.trace',
} as const

function pageOf(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value < 100_000 ? value : undefined
}

/**
 * Разделы меню Platform, куда пакет AQSI приносит окна (UI-SECTIONS). Пакет
 * `platform-ui` не импортируется: имена сверяет реестр окон при сборке
 * приложения — раздел без входа роняет её.
 */
const SECTIONS = {
  rmk: 'rmk',
  receipts: 'receipts',
  salesStatistics: 'sales-statistics',
  shifts: 'shifts',
  nomenclature: 'nomenclature',
  structure: 'structure',
  prices: 'prices',
} as const

/** Вкладка раздела: окно для каждого кабинета AQSI с правом окна (право окна). */
function tabOf(section: string, capability: string, label: string, order: number): WindowSectionPlace {
  return { id: section, tab: { resourceKind: AQSI_KIND, capability, label, order } }
}

/**
 * Место каждого окна в разделах — одной таблицей. РМК — ОДНО окно кабинета во
 * вкладке раздела «РМК» (UI-RMK: выбор кассы → смена → продажа); чеки,
 * инциденты и расхождения — «Чеков», каталог — «Номенклатуры». Окно выбора
 * кабинета каталога относится к своему разделу.
 */
const SECTION_PLACES: Readonly<Record<string, WindowSectionPlace>> = {
  [AQSI_WINDOWS.rmk]: tabOf(SECTIONS.rmk, PURPOSE_CAPABILITY.rmk, 'РМК', 10),
  [AQSI_WINDOWS.printSettings]: { id: SECTIONS.rmk },
  [AQSI_WINDOWS.trace]: tabOf(SECTIONS.rmk, TRACE_CAPABILITY, 'Диагностика продаж', 40),
  [AQSI_WINDOWS.saleScanner]: { id: SECTIONS.rmk },
  [AQSI_WINDOWS.saleResolve]: { id: SECTIONS.rmk },
  [AQSI_WINDOWS.recoveryProof]: { id: SECTIONS.rmk },
  [AQSI_WINDOWS.receipts]: tabOf(SECTIONS.receipts, PURPOSE_CAPABILITY.receipts, 'Чеки', 10),
  // Сводки — отдельный пункт меню «Статистика по продажам» по праву `sales.read`; «Чеки» открыты всем, кто видит раздел (`receipts.read`).
  [AQSI_WINDOWS.salesStatistics]: tabOf(SECTIONS.salesStatistics, SALES_READ_CAPABILITY, 'Статистика по продажам', 10),
  // Смены — отдельный пункт меню «Смены» по праву чтения чеков (`receipts.read`), не по `sales.read`.
  [AQSI_WINDOWS.shifts]: tabOf(SECTIONS.shifts, PURPOSE_CAPABILITY.receipts, 'Смены', 10),
  [AQSI_WINDOWS.receiptAttention]: tabOf(SECTIONS.receipts, SALES_READ_CAPABILITY, 'Требуют внимания', 13),
  [AQSI_WINDOWS.parkedSales]: tabOf(SECTIONS.receipts, SALES_READ_CAPABILITY, 'Отложенные продажи', 14),
  [AQSI_WINDOWS.refundLookup]: tabOf(SECTIONS.receipts, REFUNDS_CAPABILITY, 'Возврат', 15),
  [AQSI_WINDOWS.refundSearch]: { id: SECTIONS.receipts },
  [AQSI_WINDOWS.slipRefund]: { id: SECTIONS.receipts },
  [AQSI_WINDOWS.incidents]: tabOf(SECTIONS.receipts, PURPOSE_CAPABILITY.rmk, 'Инциденты продаж', 20),
  [AQSI_WINDOWS.discrepancies]: tabOf(SECTIONS.receipts, PURPOSE_CAPABILITY.rmk, 'Расхождения', 30),
  [AQSI_WINDOWS.receipt]: { id: SECTIONS.receipts },
  [AQSI_WINDOWS.refund]: { id: SECTIONS.receipts },
  [AQSI_WINDOWS.catalog]: tabOf(SECTIONS.nomenclature, PURPOSE_CAPABILITY.catalog, 'Каталог', 20),
  [AQSI_WINDOWS.catalogSource]: { id: SECTIONS.nomenclature },
  [AQSI_WINDOWS.good]: { id: SECTIONS.nomenclature },
  [AQSI_WINDOWS.delivery]: { id: SECTIONS.nomenclature },
  [AQSI_WINDOWS.createDefaults]: { id: SECTIONS.nomenclature },
  [AQSI_WINDOWS.structure]: tabOf(SECTIONS.structure, STRUCTURE_CAPABILITY, 'Структура', 20),
  [AQSI_WINDOWS.prices]: tabOf(SECTIONS.prices, PRICES_CAPABILITY, 'Цены', 20),
}

function placed(descriptor: WindowDescriptor<AqsiUiState>): WindowDescriptor<AqsiUiState> {
  const section = SECTION_PLACES[descriptor.kind]
  return section === undefined ? descriptor : { ...descriptor, section }
}

function receiptPane(kind: string, path: string, label: string): WindowDescriptor<AqsiUiState> {
  return { kind, scope: 'resource', paths: [path], label,
    capture: (state, params) => ({ view: { ...selectReceiptsView(state.receipts, params.rid ?? ''), cursors: [], page: 0 } }),
    restore: (snapshot, params) => {
      const view = decodeReceiptsView(snapshot.view)
      return view === undefined || params.rid === undefined ? [] : [viewRestored({ resourceId: params.rid, view })]
    },
  }
}

const nativeWindows: readonly WindowDescriptor<AqsiUiState>[] = [
  receiptPane(AQSI_WINDOWS.salesStatistics, '/aqsi/:rid/sales-statistics', 'Статистика по продажам'),
  receiptPane(AQSI_WINDOWS.receiptAttention, '/aqsi/:rid/receipt-attention', 'Требуют внимания'),
  receiptPane(AQSI_WINDOWS.parkedSales, '/aqsi/:rid/parked-sales', 'Отложенные продажи'),
  {
    kind: AQSI_WINDOWS.printSettings, scope: 'resource',
    paths: ['/aqsi/:rid/rmk/registers/:deviceId/print-settings'],
    label: 'Настройки РМК', size: 'md',
  },
  {
    kind: AQSI_WINDOWS.catalogSource,
    scope: 'global',
    paths: ['/aqsi/catalog'],
    label: 'Каталог AQSI',
  },
  {
    // UI-RMK: одно окно РМК кабинета; выбор кассы — блок «Касса» окна (RP-FB22).
    kind: AQSI_WINDOWS.rmk,
    scope: 'resource',
    paths: ['/aqsi/:rid/rmk'],
    label: 'РМК',
  },
  {
    kind: AQSI_WINDOWS.catalog,
    scope: 'resource',
    paths: ['/aqsi/:rid/catalog'],
    label: 'Каталог AQSI',
    // Вкладка помнит страницу каталога ЭТОГО кабинета — выбор человека, не товары.
    capture: (state, params) => ({ page: catalogIntentOf(state.catalog, params.rid ?? '').page }),
    restore: (snapshot, params): UnknownAction[] => {
      const page = pageOf(snapshot.page)
      return page === undefined || params.rid === undefined
        ? []
        : [catalogPageChanged({ resourceId: params.rid, page })]
    },
  },
  {
    kind: AQSI_WINDOWS.good,
    scope: 'resource',
    paths: ['/aqsi/:rid/catalog/:key'],
    label: 'Товар AQSI',
    size: 'lg',
    source: { id: 'aqsi', label: 'AQSI' },
  },
  {
    kind: AQSI_WINDOWS.prices,
    scope: 'resource',
    paths: ['/aqsi/:rid/prices'],
    label: 'Цены AQSI',
    // Виды цен кабинета — фиксированный список без выбора и поиска: запоминать на вкладке нечего.
  },
  {
    kind: AQSI_WINDOWS.receipts,
    scope: 'resource',
    paths: ['/aqsi/:rid/receipts'],
    label: 'Чеки',
    // Вкладка помнит отбор — выбор человека, а не чеки (CLAUDE.md §7).
    capture: (state, params) => {
      const { draft, applied, tab } = selectReceiptsView(state.receipts, params.rid ?? '')
      // Курсоры не запоминаем: вкладка живёт дольше курсора на сервере.
      return {
        view: {
          draft,
          applied,
          cursors: [],
          page: 0,
          ...(tab !== undefined ? { tab } : {}),
        },
      }
    },
    restore: (snapshot, params) => {
      const view = decodeReceiptsView(snapshot.view)
      return view === undefined || params.rid === undefined ? [] : [viewRestored({ resourceId: params.rid, view })]
    },
  },
  {
    kind: AQSI_WINDOWS.shifts,
    scope: 'resource',
    // Второй адрес — прежняя вкладка «Мои смены» «Чеков»: старые закладки открывают смены, а не пустое окно.
    paths: ['/aqsi/:rid/shifts', '/aqsi/:rid/receipt-shifts'],
    label: 'Смены',
    // Вкладка помнит отбор — выбор человека, а не смены (CLAUDE.md §7).
    capture: (state, params) => ({ view: selectShiftsView(state.shifts, params.rid ?? '') }),
    restore: (snapshot, params) => {
      const view = decodeShiftsView(snapshot.view)
      return view === undefined || params.rid === undefined ? [] : [shiftsViewRestored({ resourceId: params.rid, view })]
    },
  },
  {
    kind: AQSI_WINDOWS.receipt,
    scope: 'resource',
    paths: ['/aqsi/:rid/receipts/:key'],
    label: 'Чек',
    title: (params) => (params.key === undefined ? 'Чек' : `Чек ${params.key}`),
    size: 'lg',
  },
  {
    kind: AQSI_WINDOWS.delivery,
    scope: 'resource',
    paths: ['/aqsi/:rid/delivery'],
    label: 'Доставка каталога',
    size: 'lg',
    capture: (state, params) => ({ filter: selectOperationFilter(state.delivery, params.rid ?? '') }),
    restore: (snapshot, params) => {
      const filter = decodeOperationFilter(snapshot.filter)
      return filter === undefined || params.rid === undefined ? [] : [filterChosen({ resourceId: params.rid, filter })]
    },
  },
  {
    kind: AQSI_WINDOWS.createDefaults,
    scope: 'resource',
    paths: ['/aqsi/:rid/create-defaults'],
    label: 'Создание по умолчанию',
    size: 'md',
  },
  // Видоискатель во весь экран (карточка 267): под окном, открытым поверх, остаётся смонтированным.
  {
    kind: AQSI_WINDOWS.saleScanner,
    scope: 'resource',
    paths: ['/aqsi/:rid/sale/scanner'],
    label: 'Сканер',
    size: 'screen',
  },
  {
    kind: AQSI_WINDOWS.saleResolve,
    scope: 'resource',
    paths: ['/aqsi/:rid/operations/:operationId/resolve'],
    label: 'Ручная сверка',
    size: 'md',
  },
  // UC1d: возврат — отдельное окно чека этого rid (одно на чек); переключение кабинета его не меняет.
  {
    kind: AQSI_WINDOWS.refund,
    scope: 'resource',
    paths: ['/aqsi/:rid/receipts/:key/refund'],
    label: 'Возврат',
    title: (params) => (params.key === undefined ? 'Возврат' : `Возврат по чеку ${params.key}`),
    size: 'lg',
  },
  {
    kind: AQSI_WINDOWS.refundLookup,
    scope: 'resource',
    paths: ['/aqsi/:rid/refund'],
    label: 'Возврат по номеру чека',
  },
  // RP-FB74, этап 2: окна, открываемые из «Действия» → «Возврат» в РМК, поверх РМК (корзина и режим РМК не теряются).
  {
    kind: AQSI_WINDOWS.refundSearch,
    scope: 'resource',
    paths: ['/aqsi/:rid/refund-search'],
    label: 'Стандартный возврат',
    size: 'lg',
  },
  {
    kind: AQSI_WINDOWS.slipRefund,
    scope: 'resource',
    paths: ['/aqsi/:rid/slip-refund'],
    label: 'Возврат по слип-чеку',
    size: 'md',
  },
  {
    kind: AQSI_WINDOWS.incidents,
    scope: 'resource',
    paths: ['/aqsi/:rid/incidents'],
    label: 'Инциденты продаж',
  },
  {
    kind: AQSI_WINDOWS.recoveryProof,
    scope: 'resource',
    paths: ['/aqsi/:rid/sales/:saleId/recovery-proof'],
    label: 'Доказательство восстановления',
    size: 'md',
  },
  {
    kind: AQSI_WINDOWS.discrepancies,
    scope: 'resource',
    paths: ['/aqsi/:rid/discrepancies'],
    label: 'Расхождения чеков',
    capture: (state, params) => ({ limit: discrepancyLimitOf(state.incidents, params.rid ?? '') }),
    restore: (snapshot, params): UnknownAction[] => {
      const limit = decodeDiscrepancyLimit(snapshot.limit)
      return limit === undefined || params.rid === undefined
        ? []
        : [discrepancyLimitChosen({ resourceId: params.rid, limit })]
    },
  },
  {
    kind: AQSI_WINDOWS.structure,
    scope: 'resource',
    paths: ['/aqsi/:rid/structure'],
    label: 'Структура AQSI',
  },
  {
    // Выбранная продажа — в адресе (CLAUDE.md §7): список и разбор — один вид окна.
    kind: AQSI_WINDOWS.trace,
    scope: 'resource',
    paths: ['/aqsi/:rid/trace', '/aqsi/:rid/trace/:saleId'],
    label: 'Диагностика продаж',
    title: (params) => (params.saleId === undefined ? 'Диагностика продаж' : `Разбор продажи ${params.saleId}`),
  },
]

export const aqsiWindows: readonly WindowDescriptor<AqsiUiState>[] = nativeWindows.map(placed)
